/**
 * A aritmética dos relatórios da clínica, num lugar só.
 *
 * Os dois adaptadores leem as mesmas linhas e precisam chegar ao mesmo número —
 * pela mesma razão de `relatoriosFinanceiros.ts`. Aqui ficam as contas de
 * agenda, atendimento e funil; lá, as de competência e caixa.
 *
 * **A taxa de ocupação depende da jornada declarada.** Ocupação é hora agendada
 * sobre hora aberta, e a hora aberta vem de Configurações › Agenda. Com jornada,
 * o número é real e a barra por dia mede capacidade. **Sem** jornada, não há
 * denominador: em vez de inventar um, a distribuição volta a comparar os dias
 * ENTRE SI e a tela diz isso com todas as letras.
 */

import { janelaDeMeses } from './relatoriosFinanceiros';
import { horasNoPeriodo, minutosDoDia } from './jornada';
import type {
    CargaDeRelatorios, DiaDeFuncionamento, FiltroDeRelatorios, Indicador, Lead, LinhaDeRanking,
    LinhaDoDiaDaSemana, Movimentacao, Orcamento, PontoDaSerie, RelatorioClinico, RelatorioComercial,
    StatusDoAgendamento,
} from './tipos';

/** O agendamento como o relatório precisa dele — o mínimo, dos dois adaptadores. */
export interface AgendamentoDoRelatorio {
    inicioEm: string;
    duracaoMin: number;
    status: StatusDoAgendamento;
    procedimento: string;
    profissional: string;
    /** Duração executada, quando a consulta foi aberta e encerrada. */
    duracaoRealMin?: number;
    comFicha?: boolean;
}

export interface DadosDoRelatorio {
    agendamentos: AgendamentoDoRelatorio[];
    /** Preço de tabela por nome de procedimento, em centavos. */
    precos: Record<string, number>;
    movimentacoes: Movimentacao[];
    leads: Lead[];
    orcamentos: Orcamento[];
    /** Jornada declarada em Configurações. Vazia quando a clínica não declarou. */
    jornada: DiaDeFuncionamento[];
}

const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const FUSO = 'America/Sao_Paulo';

function dia(iso: string): string {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: FUSO });
}

function somarDias(diaIso: string, dias: number): string {
    const d = new Date(`${diaIso}T12:00:00-03:00`);
    d.setDate(d.getDate() + dias);
    return d.toLocaleDateString('en-CA', { timeZone: FUSO });
}

/** O recorte pedido e o imediatamente anterior, que é com quem ele se compara. */
export function janelaDoPeriodo(periodo: FiltroDeRelatorios['periodo'], hoje: string) {
    if (periodo === 'semana') {
        const d = new Date(`${hoje}T12:00:00-03:00`);
        const inicio = somarDias(hoje, -((d.getDay() + 6) % 7));
        return {
            inicio,
            fim: somarDias(inicio, 6),
            inicioAnterior: somarDias(inicio, -7),
            fimAnterior: somarDias(inicio, -1),
        };
    }
    if (periodo === 'mes') {
        const mes = hoje.slice(0, 7);
        const [ano, m] = mes.split('-').map(Number);
        const primeiro = `${mes}-01`;
        const ultimo = new Date(Date.UTC(ano ?? 0, m ?? 1, 0)).toISOString().slice(0, 10);
        const anterior = new Date(Date.UTC(ano ?? 0, (m ?? 1) - 2, 1));
        const mesAnterior = anterior.toISOString().slice(0, 7);
        const ultimoAnterior = new Date(Date.UTC(anterior.getUTCFullYear(), anterior.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
        return { inicio: primeiro, fim: ultimo, inicioAnterior: `${mesAnterior}-01`, fimAnterior: ultimoAnterior };
    }
    const ano = Number(hoje.slice(0, 4));
    return {
        inicio: `${ano}-01-01`,
        fim: `${ano}-12-31`,
        inicioAnterior: `${ano - 1}-01-01`,
        fimAnterior: `${ano - 1}-12-31`,
    };
}

/** Variação percentual formatada; vazio quando não há base de comparação. */
function variacao(atual: number, anterior: number): { variacao?: string; tendencia?: Indicador['tendencia'] } {
    if (anterior === 0) return atual === 0 ? {} : { variacao: 'sem base anterior', tendencia: 'neutral' };
    const delta = ((atual - anterior) / anterior) * 100;
    return {
        variacao: `${delta >= 0 ? '+' : ''}${delta.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
        tendencia: delta >= 0 ? 'up' : 'down',
    };
}

const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const porcento = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

function receitaDe(agendamentos: AgendamentoDoRelatorio[], precos: Record<string, number>): number {
    return agendamentos.reduce((s, a) => s + (precos[a.procedimento] ?? 0), 0);
}

function rankingDe(agendamentos: AgendamentoDoRelatorio[], precos: Record<string, number>): LinhaDeRanking[] {
    const porProcedimento = new Map<string, { quantidade: number; receitaCentavos: number }>();
    for (const a of agendamentos) {
        const atual = porProcedimento.get(a.procedimento) ?? { quantidade: 0, receitaCentavos: 0 };
        atual.quantidade += 1;
        atual.receitaCentavos += precos[a.procedimento] ?? 0;
        porProcedimento.set(a.procedimento, atual);
    }
    const total = [...porProcedimento.values()].reduce((s, v) => s + v.receitaCentavos, 0);
    return [...porProcedimento.entries()]
        .map(([procedimento, v]) => ({
            procedimento,
            quantidade: v.quantidade,
            receitaCentavos: v.receitaCentavos,
            ticketCentavos: v.quantidade ? Math.round(v.receitaCentavos / v.quantidade) : 0,
            participacao: total ? Math.round((v.receitaCentavos / total) * 100) : 0,
        }))
        .sort((a, b) => b.receitaCentavos - a.receitaCentavos);
}

/** Quantas vezes cada dia da semana cai no período — cinco terças ou quatro. */
function ocorrenciasPorDiaDaSemana(inicio: string, fim: string): number[] {
    const contagem = [0, 0, 0, 0, 0, 0, 0];
    const corrente = new Date(`${inicio}T12:00:00-03:00`);
    const ultimo = new Date(`${fim}T12:00:00-03:00`);
    while (corrente <= ultimo) {
        contagem[corrente.getDay()] = (contagem[corrente.getDay()] ?? 0) + 1;
        corrente.setDate(corrente.getDate() + 1);
    }
    return contagem;
}

function distribuicaoPorDia(
    agendamentos: AgendamentoDoRelatorio[],
    jornada: DiaDeFuncionamento[],
    janela: { inicio: string; fim: string },
): LinhaDoDiaDaSemana[] {
    const linhas = DIAS.map((nome) => ({ dia: nome, agendamentos: 0, horas: 0, participacao: 0 }));
    for (const a of agendamentos) {
        const indice = new Date(a.inicioEm).getDay();
        const linha = linhas[indice];
        if (!linha) continue;
        linha.agendamentos += 1;
        linha.horas += (a.duracaoRealMin ?? a.duracaoMin) / 60;
    }

    /* Com jornada, o denominador do dia é o expediente daquele dia da semana
       SOMADO no período: uma quarta a mais no mês é uma quarta a mais de
       capacidade. Sem jornada, resta comparar com o dia mais cheio. */
    const temJornada = jornada.some((d) => d.faixas.length > 0);
    const ocorrencias = ocorrenciasPorDiaDaSemana(janela.inicio, janela.fim);
    const teto = Math.max(...linhas.map((l) => l.horas), 0);

    return linhas
        .map((l, i) => {
            const abertas = temJornada ? (minutosDoDia(jornada, i) / 60) * (ocorrencias[i] ?? 0) : 0;
            return {
                ...l,
                horas: Math.round(l.horas * 10) / 10,
                participacao: teto ? Math.round((l.horas / teto) * 100) : 0,
                ...(temJornada
                    ? {
                        horasAbertas: Math.round(abertas * 10) / 10,
                        ocupacao: abertas ? Math.round((l.horas / abertas) * 100) : 0,
                    }
                    : {}),
            };
        })
        /* O dia que a clínica não abre e em que ninguém marcou nada é ruído:
           sai da lista. Fechado COM agendamento fica — encaixe fora do
           expediente é justamente o que a leitura precisa mostrar. */
        .filter((l, i) => l.agendamentos > 0 || (temJornada ? minutosDoDia(jornada, i) > 0 : i >= 1 && i <= 5));
}

function serieDeReceita(movimentacoes: Movimentacao[], mesFinal: string): PontoDaSerie[] {
    return janelaDeMeses(mesFinal, 12).map((mes) => ({
        mes,
        valorCentavos: movimentacoes
            .filter((m) => m.tipo === 'receber' && m.pagoEm.startsWith(mes))
            .reduce((s, m) => s + m.liquidoCentavos, 0),
    }));
}

function relatorioClinico(agendamentos: AgendamentoDoRelatorio[]): RelatorioClinico {
    const concluidos = agendamentos.filter((a) => a.status === 'concluido');
    const comDuracao = concluidos.filter((a) => a.duracaoRealMin !== undefined);
    const porProfissional = new Map<string, { quantidade: number; horas: number }>();

    for (const a of concluidos) {
        if (!a.profissional) continue;
        const atual = porProfissional.get(a.profissional) ?? { quantidade: 0, horas: 0 };
        atual.quantidade += 1;
        atual.horas += (a.duracaoRealMin ?? a.duracaoMin) / 60;
        porProfissional.set(a.profissional, atual);
    }

    return {
        concluidos: concluidos.length,
        faltas: agendamentos.filter((a) => a.status === 'faltou').length,
        cancelados: agendamentos.filter((a) => a.status === 'cancelado').length,
        /* A média sai do que foi EXECUTADO quando há registro; sem nenhuma
           consulta cronometrada, cai na duração prevista — e o rótulo da tela
           diz de onde veio. */
        duracaoMediaMin: Math.round(
            (comDuracao.length ? comDuracao : concluidos).reduce((s, a) => s + (a.duracaoRealMin ?? a.duracaoMin), 0)
            / Math.max(1, (comDuracao.length ? comDuracao : concluidos).length),
        ),
        comFicha: concluidos.filter((a) => a.comFicha).length,
        semFicha: concluidos.filter((a) => !a.comFicha).length,
        porProfissional: [...porProfissional.entries()]
            .map(([profissional, v]) => ({ profissional, quantidade: v.quantidade, horas: Math.round(v.horas * 10) / 10 }))
            .sort((a, b) => b.quantidade - a.quantidade),
    };
}

function relatorioComercial(leads: Lead[], orcamentos: Orcamento[], inicio: string, fim: string): RelatorioComercial {
    /* Lead e orçamento guardam DATA (`YYYY-MM-DD`), não instante. Passar isso
       por `new Date()` interpreta como meia-noite UTC e, no fuso da clínica,
       joga o registro para o dia anterior — o lead de dia 1 sairia do mês. */
    const noPeriodo = (quando?: string) => {
        if (!quando) return false;
        const d = quando.length === 10 ? quando : dia(quando);
        return d >= inicio && d <= fim;
    };
    const doPeriodo = leads.filter((l) => noPeriodo(l.criadoEm));
    const ganhos = doPeriodo.filter((l) => l.situacao === 'ganho');
    const perdidos = doPeriodo.filter((l) => l.situacao === 'perdido');
    const decididos = ganhos.length + perdidos.length;

    const orcamentosDoPeriodo = orcamentos.filter((o) => noPeriodo(o.criadoEm));
    const aprovados = orcamentosDoPeriodo.filter((o) => o.status === 'aprovado');

    const porOrigem = new Map<string, number>();
    for (const l of doPeriodo) {
        const origem = l.origem ?? 'Sem origem';
        porOrigem.set(origem, (porOrigem.get(origem) ?? 0) + 1);
    }

    return {
        leads: doPeriodo.length,
        ganhos: ganhos.length,
        perdidos: perdidos.length,
        /* Conversão é sobre o que SAIU do funil: contar quem ainda está em
           negociação como perda faria a taxa cair sozinha durante o mês. */
        conversao: decididos ? Math.round((ganhos.length / decididos) * 100) : 0,
        orcamentos: orcamentosDoPeriodo.length,
        aprovados: aprovados.length,
        valorAprovadoCentavos: aprovados.reduce((s, o) => s + o.totalCentavos, 0),
        porOrigem: [...porOrigem.entries()]
            .map(([origem, quantidade]) => ({ origem, quantidade }))
            .sort((a, b) => b.quantidade - a.quantidade),
    };
}

export function montarRelatorio(
    dados: DadosDoRelatorio,
    filtro: FiltroDeRelatorios,
    hoje: string,
): Omit<CargaDeRelatorios, 'profissionais'> {
    const janela = janelaDoPeriodo(filtro.periodo, hoje);

    const doProfissional = (a: AgendamentoDoRelatorio) => !filtro.profissional || a.profissional === filtro.profissional;
    const entre = (a: AgendamentoDoRelatorio, inicio: string, fim: string) => dia(a.inicioEm) >= inicio && dia(a.inicioEm) <= fim;

    /* Bloqueio não é atendimento: ele ocupa a agenda, mas não vira receita nem
       entra em taxa de falta. */
    const uteis = dados.agendamentos.filter((a) => a.status !== 'bloqueio' && doProfissional(a));
    const doPeriodo = uteis.filter((a) => entre(a, janela.inicio, janela.fim));
    const doAnterior = uteis.filter((a) => entre(a, janela.inicioAnterior, janela.fimAnterior));

    const concluidos = doPeriodo.filter((a) => a.status === 'concluido');
    const concluidosAntes = doAnterior.filter((a) => a.status === 'concluido');
    const receita = receitaDe(concluidos, dados.precos);
    const receitaAntes = receitaDe(concluidosAntes, dados.precos);
    const faltas = doPeriodo.filter((a) => a.status === 'faltou').length;
    const faltasAntes = doAnterior.filter((a) => a.status === 'faltou').length;
    const taxaDeFalta = doPeriodo.length ? (faltas / doPeriodo.length) * 100 : 0;
    const taxaAnterior = doAnterior.length ? (faltasAntes / doAnterior.length) * 100 : 0;
    const ticket = concluidos.length ? Math.round(receita / concluidos.length) : 0;
    const ticketAntes = concluidosAntes.length ? Math.round(receitaAntes / concluidosAntes.length) : 0;
    const horas = doPeriodo.reduce((s, a) => s + (a.duracaoRealMin ?? a.duracaoMin) / 60, 0);
    const horasAntes = doAnterior.reduce((s, a) => s + (a.duracaoRealMin ?? a.duracaoMin) / 60, 0);

    /* O denominador da ocupação: as horas que a clínica ABRE no período, dia a
       dia. Sem jornada declarada é zero, e aí não há taxa — não uma taxa zero,
       que diria "ninguém marcou nada" no lugar de "ninguém declarou o
       expediente". */
    const horasAbertas = horasNoPeriodo(dados.jornada, janela.inicio, janela.fim);
    const horasAbertasAntes = horasNoPeriodo(dados.jornada, janela.inicioAnterior, janela.fimAnterior);
    const ocupacao = horasAbertas ? Math.round((horas / horasAbertas) * 1000) / 10 : undefined;
    const ocupacaoAntes = horasAbertasAntes ? (horasAntes / horasAbertasAntes) * 100 : 0;

    const clinico = relatorioClinico(doPeriodo);
    const comercial = relatorioComercial(dados.leads, dados.orcamentos, janela.inicio, janela.fim);

    const recebido = dados.movimentacoes
        .filter((m) => m.tipo === 'receber' && m.pagoEm >= janela.inicio && m.pagoEm <= janela.fim)
        .reduce((s, m) => s + m.liquidoCentavos, 0);
    const recebidoAntes = dados.movimentacoes
        .filter((m) => m.tipo === 'receber' && m.pagoEm >= janela.inicioAnterior && m.pagoEm <= janela.fimAnterior)
        .reduce((s, m) => s + m.liquidoCentavos, 0);
    const pago = dados.movimentacoes
        .filter((m) => m.tipo === 'pagar' && m.pagoEm >= janela.inicio && m.pagoEm <= janela.fim)
        .reduce((s, m) => s + m.valorCentavos, 0);

    const indicadores: Indicador[] = filtro.aba === 'financeiro'
        ? [
            { id: 'recebido', rotulo: 'Recebido', valor: reais(recebido), apoio: 'líquido de taxa', icone: 'circle-dollar-sign', ...variacao(recebido, recebidoAntes) },
            { id: 'pago', rotulo: 'Pago', valor: reais(pago), apoio: 'saídas do período', icone: 'receipt' },
            { id: 'resultado', rotulo: 'Resultado', valor: reais(recebido - pago), apoio: 'recebido menos pago', tendencia: recebido >= pago ? 'up' : 'down', icone: 'chart-no-axes-column' },
            { id: 'agendado', rotulo: 'Receita agendada', valor: reais(receita), apoio: `${concluidos.length} atendimentos concluídos`, icone: 'calendar-check', ...variacao(receita, receitaAntes) },
        ]
        : filtro.aba === 'clinico'
            ? [
                { id: 'concluidos', rotulo: 'Atendimentos', valor: String(clinico.concluidos), apoio: 'concluídos no período', icone: 'stethoscope', ...variacao(clinico.concluidos, concluidosAntes.length) },
                { id: 'duracao', rotulo: 'Duração média', valor: `${clinico.duracaoMediaMin} min`, apoio: 'por atendimento', icone: 'clock' },
                { id: 'ficha', rotulo: 'Com ficha preenchida', valor: String(clinico.comFicha), apoio: `${clinico.semFicha} sem registro`, tendencia: clinico.semFicha ? 'down' : 'up', icone: 'clipboard-pen' },
                { id: 'horas', rotulo: 'Horas de agenda', valor: `${Math.round(horas * 10) / 10} h`, apoio: 'no período', icone: 'calendar-days', ...variacao(horas, horasAntes) },
            ]
            : filtro.aba === 'comercial'
                ? [
                    { id: 'leads', rotulo: 'Leads', valor: String(comercial.leads), apoio: 'criados no período', icone: 'target' },
                    { id: 'conversao', rotulo: 'Conversão', valor: porcento(comercial.conversao), apoio: `${comercial.ganhos} ganhos · ${comercial.perdidos} perdidos`, tendencia: comercial.conversao >= 50 ? 'up' : 'down', icone: 'trending-up' },
                    { id: 'orcamentos', rotulo: 'Orçamentos', valor: String(comercial.orcamentos), apoio: `${comercial.aprovados} aprovados`, icone: 'file-text' },
                    { id: 'aprovado', rotulo: 'Valor aprovado', valor: reais(comercial.valorAprovadoCentavos), apoio: 'orçamentos ganhos', icone: 'circle-dollar-sign' },
                ]
                : [
                    { id: 'agendamentos', rotulo: 'Agendamentos', valor: String(doPeriodo.length), apoio: `${clinico.concluidos} concluídos`, icone: 'calendar-days', ...variacao(doPeriodo.length, doAnterior.length) },
                    /* Só entra com jornada declarada. O indicador ausente é
                       mais honesto que um "0%" que ninguém sabe ler. */
                    ...(ocupacao === undefined ? [] : [{
                        id: 'ocupacao', rotulo: 'Taxa de ocupação', valor: porcento(ocupacao),
                        apoio: `${(Math.round(horas * 10) / 10).toLocaleString('pt-BR')} h de ${horasAbertas.toLocaleString('pt-BR')} h abertas`,
                        icone: 'gauge', ...variacao(ocupacao, ocupacaoAntes),
                    } satisfies Indicador]),
                    { id: 'receita', rotulo: 'Receita realizada', valor: reais(receita), apoio: 'atendimentos concluídos', icone: 'circle-dollar-sign', ...variacao(receita, receitaAntes) },
                    { id: 'ticket', rotulo: 'Ticket médio', valor: reais(ticket), apoio: 'por atendimento', icone: 'chart-no-axes-column', ...variacao(ticket, ticketAntes) },
                    /* Falta que sobe é notícia RUIM: a seta inverte de propósito. */
                    { id: 'faltas', rotulo: 'Taxa de falta', valor: porcento(Math.round(taxaDeFalta * 10) / 10), apoio: `${faltas} de ${doPeriodo.length}`, tendencia: taxaDeFalta <= taxaAnterior ? 'up' : 'down', icone: 'user-x' },
                    { id: 'horas', rotulo: 'Horas de agenda', valor: `${Math.round(horas * 10) / 10} h`, apoio: 'somando as durações', icone: 'clock', ...variacao(horas, horasAntes) },
                ];

    return {
        aba: filtro.aba,
        periodo: filtro.periodo,
        inicio: janela.inicio,
        fim: janela.fim,
        profissional: filtro.profissional,
        indicadores,
        ranking: rankingDe(concluidos, dados.precos),
        porDia: distribuicaoPorDia(doPeriodo, dados.jornada, janela),
        serie: serieDeReceita(dados.movimentacoes, janela.fim.slice(0, 7)),
        clinico,
        comercial,
        horasAbertas,
        ...(ocupacao === undefined ? {} : { ocupacao }),
    };
}
